import React, { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  SelectChangeEvent
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ImageUploader from './ImageUploader'
import AspectRatioSelector from './AspectRatioSelector'
import axios from 'axios'

interface ConversionMetadata {
  format: string
  width: number
  height: number
  size_kb: number
  original_size: [number, number]
}

const FormatConverter: React.FC = () => {
  const [sourceImage, setSourceImage] = useState<string>('')
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [targetFormat, setTargetFormat] = useState<string>('PNG')
  const [quality, setQuality] = useState<number>(85)
  const [aspectRatio, setAspectRatio] = useState<string>('none')
  const [convertedImage, setConvertedImage] = useState<string>('')
  const [metadata, setMetadata] = useState<ConversionMetadata | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const handleImageUpload = (imageData: string, file: File) => {
    setSourceImage(imageData)
    setSourceFile(file)
    setConvertedImage('')
    setMetadata(null)
    setError('')
  }

  const handleFormatChange = (event: SelectChangeEvent) => {
    setTargetFormat(event.target.value)
  }

  const handleQualityChange = (_event: Event, newValue: number | number[]) => {
    setQuality(newValue as number)
  }

  const handleConvert = async () => {
    if (!sourceImage) {
      setError('Proszę załadować obraz')
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        '/api/photo-tools/convert',
        {
          image: sourceImage,
          target_format: targetFormat,
          quality: quality,
          aspect_ratio: aspectRatio !== 'none' ? aspectRatio : null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data.status === 'success') {
        setConvertedImage(`data:image/${targetFormat.toLowerCase()};base64,${response.data.converted_image}`)
        setMetadata(response.data.metadata)
      } else {
        setError(response.data.error || 'Konwersja nie powiodła się')
      }
    } catch (err: any) {
      console.error('Conversion error:', err)
      setError(err.response?.data?.error || 'Wystąpił błąd podczas konwersji')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!convertedImage) return

    const link = document.createElement('a')
    link.download = `converted.${targetFormat.toLowerCase()}`
    link.href = convertedImage
    link.click()
  }

  const handleReset = () => {
    setSourceImage('')
    setSourceFile(null)
    setConvertedImage('')
    setMetadata(null)
    setError('')
    setTargetFormat('PNG')
    setQuality(85)
    setAspectRatio('none')
  }

  const formatSupportsQuality = ['JPEG', 'JPG', 'WEBP', 'AVIF'].includes(targetFormat)

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!sourceImage && (
        <ImageUploader
          label="Obraz do konwersji"
          onImageUpload={handleImageUpload}
          currentImage={sourceImage}
        />
      )}

      {sourceImage && !convertedImage && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Obraz źródłowy
              </Typography>
              <Box
                component="img"
                src={sourceImage}
                alt="Source"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                  objectFit: 'contain'
                }}
              />
              {sourceFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {sourceFile.name} ({Math.round(sourceFile.size / 1024)} KB)
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Format docelowy</InputLabel>
                <Select
                  value={targetFormat}
                  label="Format docelowy"
                  onChange={handleFormatChange}
                >
                  <MenuItem value="PNG">PNG (bezstratny)</MenuItem>
                  <MenuItem value="JPEG">JPEG (stratny, mały rozmiar)</MenuItem>
                  <MenuItem value="WEBP">WebP (nowoczesny, efektywny)</MenuItem>
                  <MenuItem value="AVIF">AVIF (najbardziej efektywny)</MenuItem>
                </Select>
              </FormControl>

              {formatSupportsQuality && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Jakość: {quality}%
                  </Typography>
                  <Slider
                    value={quality}
                    onChange={handleQualityChange}
                    min={1}
                    max={100}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Niższa jakość = mniejszy rozmiar pliku
                  </Typography>
                </Box>
              )}

              <AspectRatioSelector
                value={aspectRatio}
                onChange={setAspectRatio}
              />

              <Button
                variant="contained"
                onClick={handleConvert}
                disabled={loading}
                fullWidth
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Konwertuj'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                fullWidth
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {convertedImage && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Oryginał
                </Typography>
                <Box
                  component="img"
                  src={sourceImage}
                  alt="Original"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '300px',
                    objectFit: 'contain'
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Przekonwertowany ({targetFormat})
                </Typography>
                <Box
                  component="img"
                  src={convertedImage}
                  alt="Converted"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '300px',
                    objectFit: 'contain'
                  }}
                />
                {metadata && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Wymiary: {metadata.width} x {metadata.height}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Rozmiar: {metadata.size_kb} KB
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Oryginalne wymiary: {metadata.original_size[0]} x {metadata.original_size[1]}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Pobierz
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
            >
              Nowa konwersja
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FormatConverter
